import { useMemo } from 'react';
import {
  infiniteQueryOptions,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { newItemsCheckOptions } from '../shared/new-items-check';
import { campaignKeys } from '../campaign/entity';
import { getCampaigns, useCampaignOptional } from '../campaign/queries';
import type { WorkspaceEnvironment } from '../environment/entity';
import { useEnvironmentOptional } from '../environment/queries';
import {
  activityLogKeys,
  type ActivityLog,
  type ActivityLogCustomer,
  type ActivityLogFilters,
  type ActivityLogObject,
  type ActivityLogRow,
  type ActivityLogSegment,
  type ActivityLogTab,
  type NameSearchScope,
} from './entity';
import { listLogsExactly, listLogsLosslessly } from './lossless';

/** Identified-customer logs are capped at 50 per page server-side; match it for both tabs. */
const PAGE_SIZE = 50;

/** Stable fallback, so a missing `enabled_actions` doesn't break callers' memo deps. */
const NO_ENABLED_ACTIONS: readonly string[] = [];
const NO_OBJECT_TYPES: WorkspaceEnvironment['object_types'] = [];

// The generated client types the response as a union (JSON body | raw Blob/Response/…); narrow to
// the JSON page shape we actually consume by the presence of `logs`.
export type LogsResponse = Awaited<ReturnType<typeof hydraUiApi.logs.list>>;
export type LogsPage = Extract<LogsResponse, { logs: unknown }>;

function blankToUndefined(value: string): string | undefined {
  return isEmptyString(value) ? undefined : value;
}

/** Key a customer by both its external and internal id (a log may reference either). */
function indexCustomer(customerById: Map<string, ActivityLogCustomer>, customer: ActivityLogCustomer) {
  if (!isEmptyString(customer.id)) customerById.set(customer.id, customer);
  if (!isEmptyString(customer.internal_id)) customerById.set(customer.internal_id, customer);
}

/**
 * Build id → record lookups from every page's sideloaded relations. The generated type marks
 * `customers` non-optional, but the anonymous endpoint returns `null` for it at runtime — the
 * `?? []` guards against that spec/runtime mismatch.
 */
export function buildRelationLookups(pages: LogsPage[]) {
  const customerById = new Map<string, ActivityLogCustomer>();
  const objectById = new Map<string, ActivityLogObject>();
  const segmentById = new Map<number, ActivityLogSegment>();
  for (const page of pages) {
    for (const customer of page.customers ?? []) indexCustomer(customerById, customer);
    for (const object of page.objects ?? []) objectById.set(object.id, object);
    for (const segment of page.segments ?? []) segmentById.set(segment.id, segment);
  }
  return { customerById, objectById, segmentById };
}

/** segment-build logs carry the segment id in `name`. */
function segmentBuildSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  const id = Number(log.name);
  return Number.isNaN(id) ? undefined : segmentById.get(id);
}

/** membership-change logs reference a segment via `attrs.entered`/`attrs.exited` (only when exactly one). */
function membershipChangeSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  if (!isRecord(log.attrs)) return undefined;
  const record = log.attrs;
  const entered = Array.isArray(record.entered) ? record.entered : [];
  const exited = Array.isArray(record.exited) ? record.exited : [];
  const ids = [...entered, ...exited];
  return ids.length === 1 ? segmentById.get(Number(ids[0])) : undefined;
}

function resolveLogSegment(
  log: ActivityLog,
  segmentById: Map<number, ActivityLogSegment>,
): ActivityLogSegment | undefined {
  if (log.type === 'segment_build') return segmentBuildSegment(log, segmentById);
  if (log.type === 'profile_segment_membership_change') return membershipChangeSegment(log, segmentById);
  return undefined;
}

/** Join each page's logs to their sideloaded customer/object/segment records. */
export function buildLogRows(pages: LogsPage[], lookups: ReturnType<typeof buildRelationLookups>): ActivityLogRow[] {
  const { customerById, objectById, segmentById } = lookups;
  return pages.flatMap((page) =>
    (page.logs ?? []).map((log) => ({
      log,
      customer: customerById.get(log.customer_id ?? log.internal_id ?? ''),
      object: isEmptyString(log.object_id ?? '') ? undefined : objectById.get(log.object_id ?? ''),
      segment: resolveLogSegment(log, segmentById),
    })),
  );
}

/**
 * One page of logs from a single (non-paginated) fetch, joined to its sideloaded records.
 * `segmentById` is what an expanded row needs to turn segment ids in a payload into links.
 */
export function useActivityLogRows(data: LogsResponse | undefined) {
  const pages = useMemo(() => (typeof data === 'object' && 'logs' in data ? [data] : []), [data]);
  const lookups = useMemo(() => buildRelationLookups(pages), [pages]);
  const rows = useMemo(() => buildLogRows(pages, lookups), [pages, lookups]);

  return { rows, segmentById: lookups.segmentById };
}

export interface UseLogsInput {
  /**
   * Numeric workspace id (== the API `environment_id`) as a string. Resolve the `last` route
   * alias before calling — the list cache key derives a number from this, and a non-numeric id
   * would key outside the `['workspace', <id>]` subtree that workspace-switch eviction clears.
   * Non-numeric input disables the query.
   */
  environmentId: string;
  tab: ActivityLogTab;
  filters: ActivityLogFilters;
  /**
   * How many entries to ask for. A panel showing the last few should say so
   * rather than reading a whole page and throwing most of it away.
   */
  limit?: number;
  /**
   * Narrow the feed to one object's activity (its internal `cio_object_id`). The object's page
   * reads its own logs through the same feed, so paging, "load new" and the rows stay one thing.
   */
  cioObjectId?: string;
  /** Narrow the feed to one person's activity (their internal `cio_id`), as a profile's Activity tab does. */
  internalId?: string;
}

type FetchLogsPageInput = Pick<UseLogsInput, 'environmentId' | 'tab' | 'filters' | 'cioObjectId' | 'internalId'> & {
  limit: number;
  continuation: string;
  signal: AbortSignal;
};

/** One page of `GET /v1/environments/{id}/logs`. An empty `continuation` reads the first page. */
function fetchLogsPage({
  environmentId,
  tab,
  filters,
  limit,
  continuation,
  cioObjectId,
  internalId,
  signal,
}: FetchLogsPageInput): Promise<LogsPage> {
  return listLogsExactly(
    environmentId,
    {
      anonymous: tab === 'anonymous' ? true : undefined,
      type: blankToUndefined(filters.type),
      name: blankToUndefined(filters.name),
      from: blankToUndefined(filters.from),
      to: blankToUndefined(filters.to),
      limit,
      continuation: blankToUndefined(continuation),
      cio_object_id: blankToUndefined(cioObjectId ?? ''),
      internal_id: blankToUndefined(internalId ?? ''),
    },
    signal,
  );
}

/** A non-numeric id (an unresolved `last` alias) must not fetch: see `logsInfiniteQueryOptions`. */
function isFetchableEnvironmentId(numericEnvironmentId: number) {
  return Number.isInteger(numericEnvironmentId) && numericEnvironmentId > 0;
}

/**
 * Infinite (cursor-paginated) options for the logs feed. `services/db` reads are usually the
 * `getX`/`useX` (`useQuery`) trio, but the logs feed accumulates pages ("load more") and "load new"
 * re-fetches all loaded pages, so it uses `useInfiniteQuery` — the sanctioned shape for
 * high-volume paginated feeds. It does not poll: `useNewLogsCheck` does. Keyed under the workspace scope for clean teardown.
 *
 * @public
 */
export const logsInfiniteQueryOptions = ({
  environmentId,
  tab,
  filters,
  limit = PAGE_SIZE,
  cioObjectId,
  internalId,
}: UseLogsInput) => {
  const numericEnvironmentId = Number(environmentId);
  return infiniteQueryOptions({
    queryKey: activityLogKeys.list(numericEnvironmentId, { tab, filters, limit, cioObjectId, internalId }),
    initialPageParam: '',
    queryFn: ({ pageParam, signal }): Promise<LogsPage> =>
      fetchLogsPage({ environmentId, tab, filters, limit, continuation: pageParam, cioObjectId, internalId, signal }),
    getNextPageParam: (lastPage: LogsPage) =>
      isEmptyString(lastPage.meta.continuation) ? undefined : lastPage.meta.continuation,
    // A non-numeric id (an unresolved `last` alias) must not fetch: it would cache under
    // `['workspace', NaN, …]`, which no workspace-switch eviction ever clears.
    enabled: isFetchableEnvironmentId(numericEnvironmentId),
  });
};

/**
 * Activity-logs feed over `GET /v1/environments/{id}/logs`. Cursor pagination via
 * `meta.continuation` ("load more"); the reply sideloads `customers` / `objects` / `segments`,
 * which are joined by id into row view-models so the cells never fetch per-row.
 *
 * @public
 */
export function useLogs(input: UseLogsInput) {
  const query = useInfiniteQuery(logsInfiniteQueryOptions(input));

  const { rows, segmentById } = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const lookups = buildRelationLookups(pages);
    // `segmentById` is also returned so the expanded-row JSON formatter can resolve the segment ids
    // in `entered`/`exited` arrays to name links.
    return { rows: buildLogRows(pages, lookups), segmentById: lookups.segmentById };
  }, [query.data]);

  return {
    rows,
    segmentById,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage,
    fetchMore: query.fetchNextPage,
    isFetchingMore: query.isFetchingNextPage,
    /** The last "load more" failed; the pages already read stay. */
    isFetchMoreError: query.isFetchNextPageError,
    refetch: query.refetch,
    /** ms epoch of the last successful fetch — feeds the "Last updated …" label. */
    lastUpdated: query.dataUpdatedAt,
    /** The newest page, which seeds the "load new" check. */
    firstPage: query.data?.pages[0],
  };
}

export interface UseNewLogsCheckInput extends Pick<
  UseLogsInput,
  'environmentId' | 'tab' | 'filters' | 'cioObjectId' | 'internalId'
> {
  /** False stops the check, for example while the list is still loading. */
  enabled: boolean;
  /** The list's first page and when it was fetched. The check starts from it, so the page loads once. */
  seed: LogsPage | undefined;
  seedUpdatedAt: number;
}

/**
 * The "load new" check for the logs feed: the first page every 15s, on its own key, with the same
 * tab and filters as the list. See `newItemsCheckOptions`.
 *
 * @public
 */
export function useNewLogsCheck({
  environmentId,
  tab,
  filters,
  cioObjectId,
  internalId,
  enabled,
  seed,
  seedUpdatedAt,
}: UseNewLogsCheckInput) {
  const numericEnvironmentId = Number(environmentId);
  return useQuery(
    newItemsCheckOptions({
      queryKey: activityLogKeys.newCheck(numericEnvironmentId, {
        tab,
        filters,
        limit: PAGE_SIZE,
        cioObjectId,
        internalId,
      }),
      queryFn: ({ signal }) =>
        fetchLogsPage({
          environmentId,
          tab,
          filters,
          limit: PAGE_SIZE,
          continuation: '',
          cioObjectId,
          internalId,
          signal,
        }),
      enabled: enabled && isFetchableEnvironmentId(numericEnvironmentId),
      seed,
      seedUpdatedAt,
    }),
  );
}

/**
 * Workspace `sent_events` / `sent_attributes` (the Activity Type name filter's options), the
 * `identifiers` config (`{ email, id, cio_id, … }` → numeric state; `0` = disabled) used to count
 * how many changed attributes are identifiers in the Activity Name, and the raw `enabled_actions`
 * (which SMS channels the Activity Type filter offers), and the workspace's object types, which the
 * relationship-attribute types are filtered by.
 *
 * Built on the shared `environment` entity: workspace-scoped cache with `last`-alias handling.
 *
 * @public
 */
export function useActivityLogEnvironment(environmentId: string) {
  const query = useEnvironmentOptional(Number(environmentId));
  const env = query.data?.environment;
  return {
    sentEvents: env?.sent_events ?? [],
    sentAttributes: env?.sent_attributes ?? [],
    // `identifiers` is loosely typed on the wire; narrowed to the state map the identifier count needs.
    identifiers:
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      (env?.identifiers ?? {}) as Record<string, number>,
    enabledActions: env?.enabled_actions ?? NO_ENABLED_ACTIONS,
    objectTypes: query.data?.object_types ?? NO_OBJECT_TYPES,
  };
}

/** A webhook campaign for the Activity Log name filter — `id` is the value sent as the `name` filter. */
export interface WebhookCampaignOption {
  id: string;
  name: string;
}

/**
 * Webhook-triggered campaigns for the Activity Log's webhook name filter, searched server-side. For
 * `webhook_event` the logs API's `name` filter matches the campaign id (not a free-text name), so
 * this picker resolves a campaign name → its id. The campaigns list pages at 20 with no size
 * override, so the whole set can't be loaded for a client-side filter — instead each search term
 * (debounced by the caller) refetches with `search`.
 *
 * Built on the `campaign` entity's list read (non-suspense, per the `useXOptional` pattern): each
 * (workspace, search) page caches under `campaignKeys.list` and cluster-populates the campaign
 * detail slots it returns.
 *
 * @public
 */
export function useWebhookCampaigns(environmentId: string, search: string) {
  const workspaceId = Number(environmentId);
  const qc = useQueryClient();
  const active = Number.isInteger(workspaceId) && workspaceId > 0;
  const params = { page: 1, type: 'webhook', search: blankToUndefined(search) };
  const query = useQuery({
    queryKey: active ? campaignKeys.list(workspaceId, params) : ['campaign', 'list', 'inactive'],
    queryFn: active ? () => getCampaigns(workspaceId, params, qc) : skipToken,
    staleTime: 60_000,
  });
  const campaigns: WebhookCampaignOption[] = (query.data?.campaigns ?? []).map((campaign) => ({
    id: String(campaign.id),
    name: campaign.name,
  }));
  return { campaigns, isLoading: query.isLoading };
}

/**
 * Resolve a single campaign's name by id — seeds the webhook name filter's input when the filter is
 * populated from a deep link (`?type=webhook_event&name=<campaignId>`), where only the id is known.
 * Reads the `campaign` entity's by-id slot, so a campaign the cluster already loaded resolves
 * without a request.
 *
 * @public
 */
export function useCampaignName(environmentId: string, campaignId: string): string | undefined {
  const workspaceId = Number(environmentId);
  const query = useCampaignOptional({
    workspaceId,
    id: campaignId,
    enabled: Number.isInteger(workspaceId) && workspaceId > 0,
  });
  return query.data?.name;
}

/**
 * The attributes-list scope for an attribute search: nothing extra for people attributes, the
 * `objectType` for one object type's attributes, and `relationship` on top for that type's
 * relationship attributes. An object search never runs unscoped — that would list people attributes.
 */
function attributeSearchScope(scope: Exclude<NameSearchScope, { kind: 'event' }>) {
  switch (scope.kind) {
    case 'attribute':
      return {};
    case 'object_attribute':
      return { objectType: Number(scope.objectTypeId) };
    case 'relationship_attribute':
      return { objectType: Number(scope.objectTypeId), relationship: true };
    default: {
      const _exhaustive: never = scope;
      return _exhaustive;
    }
  }
}

// Fetch a page of matching names for one scope. Each list item serializes its name as `id`
// (Go `json:"id"`) on both endpoints. Kept per-endpoint (not a shared params object) so each
// generated client call keeps its own param type.
async function fetchNames(scope: NameSearchScope, workspaceId: number, term: string): Promise<string[]> {
  const environment_id = String(workspaceId);
  const query = { search: term, page: 1, size: 50 };
  if (scope.kind === 'event') {
    const raw = await hydraUiApi.events.getEventNames({ params: { path: { environment_id }, query } });
    return (raw.events ?? []).map((event) => event.id);
  }
  const raw = await hydraUiApi.attributes.list({
    params: { path: { environment_id }, query: { ...query, ...attributeSearchScope(scope) } },
  });
  return (raw.attributes ?? []).map((attribute) => attribute.id);
}

/**
 * Server-side name search for the Activity Log's Event and Attribute Change name filters. Both the
 * event_names and attributes endpoints filter names by `search` in the database, so any name in the
 * workspace is findable — unlike the bounded `sent_events` / `sent_attributes` lists the filters used
 * to fall back to, which silently hid names outside them. Only fires while there is a (debounced)
 * search term; the empty state is served from the cached list at the call site, so mounting the
 * filter costs no request. Capped at one page (the endpoint's max size); a term specific enough to
 * matter narrows well under that.
 *
 * @public
 */
export function useNameSearch(scope: NameSearchScope, environmentId: string, search: string) {
  const workspaceId = Number(environmentId);
  const term = search.trim();
  const active = Number.isInteger(workspaceId) && workspaceId > 0 && !isEmptyString(term);
  const query = useQuery({
    queryKey: active
      ? activityLogKeys.nameSearch(scope, workspaceId, term)
      : ['activity-log', 'name-search', scope.kind, 'inactive'],
    queryFn: active ? () => fetchNames(scope, workspaceId, term) : skipToken,
    staleTime: 60_000,
  });
  return { names: query.data ?? [], isLoading: query.isLoading };
}

/** The log type a webhook arriving at a campaign's endpoint is recorded as. */
const WEBHOOK_EVENT_TYPE = 'webhook_event';

/**
 * The webhooks a campaign has received, newest first. Webhooks received in
 * the same second keep the reverse of the order the logs list them in.
 */
export async function getWebhookLogs(workspaceId: number, campaignId: string): Promise<ActivityLog[]> {
  const reply = await listLogsLosslessly(workspaceId, { type: WEBHOOK_EVENT_TYPE, name: campaignId });
  return [...(reply.logs ?? [])].sort((a, b) => a.timestamp - b.timestamp).reverse();
}

export const webhookLogsQueryOptions = (workspaceId: number, campaignId: string) =>
  queryOptions({
    queryKey: activityLogKeys.webhookEvents(workspaceId, campaignId),
    queryFn: () => getWebhookLogs(workspaceId, campaignId),
    staleTime: 0,
  });

/**
 * The webhooks a campaign has received, as the activity feed reads them: numbers
 * kept as sent, the records the rows link to sideloaded, and in the feed's order.
 */
export function getCampaignWebhookActivity(workspaceId: number, campaignId: string, limit: number) {
  return listLogsExactly(workspaceId, { type: WEBHOOK_EVENT_TYPE, name: campaignId, limit });
}

export const campaignWebhookActivityQueryOptions = (workspaceId: number, campaignId: string, limit: number) =>
  queryOptions({
    queryKey: activityLogKeys.webhookActivity(workspaceId, campaignId, limit),
    queryFn: () => getCampaignWebhookActivity(workspaceId, campaignId, limit),
    staleTime: 0,
  });
